import { useCallback, Dispatch, SetStateAction } from 'react';
import { GameState, NewsItem, DisplayFixture, Fixture, CupFixture, Player, Position, SearchHistoryItem } from '../../domain/entities';
import { FixturesHelper } from '../../services/FixturesHelper';
import { getPlayerTeam, findPlayerInState, addNewsItem, createNewGame } from '../../services/GameService';
import { PlayerGenerator, maleFirstNames, femaleFirstNames, lastNames } from '../../services/playerGenerator';
import { MAX_SQUAD_SIZE, getPlayerValue, getPlayerWeeklyWage } from '../../domain/constants';
import { legends } from '../../data/legends';
import { buildRestrictedGameData } from '../../data/game-data';
import { clearAllSaves, saveAutosave } from '../../infrastructure/GameStateRepository';

type SetGameState = Dispatch<SetStateAction<GameState | { gameStarted: false; }>>;
type SetBoolean = Dispatch<SetStateAction<boolean>>;

interface UseUtilityProps {
    gameState: GameState | { gameStarted: false };
    setGameState: SetGameState;
    setHasSave?: SetBoolean;
}

const basePlayerStats = { form: 0, wage: 0, lastSeasonChange: 0, gamesPlayed: 0, goalsScored: 0, goalsConceded: 0, assists: 0, yellowCards: 0, redCards: 0, injury: null, recentRatings: [], potmAwards: 0, marketValue: 0, marketValueHistory: [] };

export const useUtility = ({ gameState, setGameState, setHasSave }: UseUtilityProps) => {

    const markNewsAsRead = useCallback((newsId?: string) => {
        setGameState(prevState => {
            if (!prevState.gameStarted) return prevState;
            const newState: GameState = JSON.parse(JSON.stringify(prevState));
            (newState as any).gameStarted = true;
            if (newsId) {
                const newsItem = newState.news.find((n: NewsItem) => n.id === newsId);
                if (newsItem) {
                    newsItem.isRead = true;
                }
            } else {
                newState.news.forEach((n: NewsItem) => n.isRead = true);
            }
            return newState;
        });
    }, [setGameState]);

    const addPlayerMoney = useCallback((amount: number) => {
        setGameState(prevState => {
            if (!prevState.gameStarted) return prevState;
            const newState: GameState = JSON.parse(JSON.stringify(prevState));
            (newState as any).gameStarted = true;
            const playerTeam = getPlayerTeam(newState);
            if (playerTeam) {
                playerTeam.finances.balance += amount;
                playerTeam.finances.history.push({
                    season: newState.season,
                    week: newState.currentWeek,
                    event: 'Debug: Added Funds',
                    amount: amount,
                });
            }
            if (!newState.debugFlags) {
                newState.debugFlags = { adsDisabled: false, moneyAdded: false, simulationUsed: false, aiPlayerUsed: false, alwaysWin: false, showHiddenInfo: false };
            }
            newState.debugFlags.moneyAdded = true;
            return newState;
        });
    }, [setGameState]);

    const setDebugFlag = useCallback((flag: keyof NonNullable<GameState['debugFlags']>) => {
        setGameState(prevState => {
            if (!prevState.gameStarted) return prevState;
            const newState: GameState = JSON.parse(JSON.stringify(prevState));
            (newState as any).gameStarted = true;
            if (!newState.debugFlags) {
                newState.debugFlags = { adsDisabled: false, moneyAdded: false, simulationUsed: false, aiPlayerUsed: false, alwaysWin: false, showHiddenInfo: false };
            }
            newState.debugFlags[flag] = true;
            return newState;
        });
    }, [setGameState]);

    const toggleDebugFlag = useCallback((flag: keyof NonNullable<GameState['debugFlags']>) => {
        setGameState(prevState => {
            if (!prevState.gameStarted) return prevState;
            const newState: GameState = JSON.parse(JSON.stringify(prevState));
            (newState as any).gameStarted = true;
            if (!newState.debugFlags) {
                newState.debugFlags = { adsDisabled: false, moneyAdded: false, simulationUsed: false, aiPlayerUsed: false, alwaysWin: false, showHiddenInfo: false };
            }
            // This toggles based on truthiness. !undefined -> true. !true -> false. !false -> true.
            (newState.debugFlags as any)[flag] = !newState.debugFlags?.[flag];
            return newState;
        });
    }, [setGameState]);

    const cacheAiPunditTake = useCallback((playerId: number, take: string) => {
        setGameState(prevState => {
            if (!prevState.gameStarted) return prevState;
            const newState: GameState = JSON.parse(JSON.stringify(prevState));
            (newState as any).gameStarted = true;
            const playerInfo = findPlayerInState(newState, playerId);
            if (playerInfo) {
                playerInfo.player.aiPunditTake = {
                    take,
                    weekGenerated: Math.floor(newState.currentWeek),
                    seasonGenerated: newState.season,
                };
            }
            return newState;
        });
    }, [setGameState]);

    const cacheAiMatchReport = useCallback((fixture: DisplayFixture, report: { title: string; message: string; }) => {
        setGameState(prevState => {
            if (!prevState.gameStarted) return prevState;
            const newState: GameState = JSON.parse(JSON.stringify(prevState));
            (newState as any).gameStarted = true;

            let foundFixture: Fixture | CupFixture | undefined;

            if (fixture.type === 'league') {
                const leagueFixtures = FixturesHelper.getAllLeagueFixtures(newState.fixtures, fixture.competition);
                if (leagueFixtures) {
                    foundFixture = leagueFixtures.find(f => f.week === fixture.week && f.home === fixture.home && f.away === fixture.away);
                }
            } else { // 'cup'
                const cupFixtures = FixturesHelper.getAllCupFixtures(newState.fixtures, fixture.competition);
                if (cupFixtures) {
                    foundFixture = cupFixtures.find(f => f.week === fixture.week && f.home === fixture.home && f.away === fixture.away);
                }
            }

            if (foundFixture && foundFixture.result) {
                foundFixture.result.aiPunditReport = report;
            }

            return newState;
        });
    }, [setGameState]);

    const cacheAiPlayerRoleAnalysis = useCallback((playerId: number, analysis: { roleName: string; roleDescription: string; }) => {
        setGameState(prevState => {
            if (!prevState.gameStarted) return prevState;
            const newState: GameState = JSON.parse(JSON.stringify(prevState));
            (newState as any).gameStarted = true;
            const playerInfo = findPlayerInState(newState, playerId);
            if (playerInfo) {
                playerInfo.player.aiRoleAnalysis = {
                    ...analysis,
                    weekGenerated: Math.floor(newState.currentWeek),
                    seasonGenerated: newState.season,
                };
            }
            return newState;
        });
    }, [setGameState]);

    const addElitePlayer = useCallback((): Promise<Player | undefined> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) {
                    resolve(undefined);
                    return prevState;
                }
                
                PlayerGenerator.initializePlayerIdCounter(prevState as GameState);

                const newState: GameState = JSON.parse(JSON.stringify(prevState));
                (newState as any).gameStarted = true;
                const playerTeam = getPlayerTeam(newState);
                if (playerTeam) {
                    if (playerTeam.squad.length >= MAX_SQUAD_SIZE) {
                        resolve(undefined);
                        return prevState;
                    }
                    const positions: Position[] = ['FWD', 'MID', 'DEF'];
                    const pos = positions[Math.floor(Math.random() * positions.length)];
                    
                    const gender = newState.gameType === 'womens' ? 'female' : 'male';
                    
                    const firstNames = gender === 'female' ? femaleFirstNames : maleFirstNames;
                    const randomFirstName = firstNames[Math.floor(Math.random() * firstNames.length)];
                    const randomLastName = lastNames[Math.floor(Math.random() * lastNames.length)];
                    const newPlayerName = `${randomFirstName} ${randomLastName}`;

                    // FIX: Pass all required arguments to generatePlayer
                    const newPlayer = PlayerGenerator.generatePlayer(newPlayerName, pos, 85 + Math.floor(Math.random() * 10), gender, newState.playerLeagueName, newState.config);
                    newPlayer.age = 18 + Math.floor(Math.random() * 4);
                    playerTeam.squad.push(newPlayer);
                    addNewsItem(newState, 'Elite Player Arrives', `${newPlayer.name}, a world-class ${newPlayer.position}, has mysteriously joined the club on a free transfer.`, 'general');
                    resolve(newPlayer);
                } else {
                    resolve(undefined);
                }
                return newState;
            });
        });
    }, [setGameState]);
    
    const addLegendPlayer = useCallback((): Promise<Player | undefined> => {
        return new Promise(resolve => {
            setGameState(prevState => {
                if (!prevState.gameStarted) {
                    resolve(undefined);
                    return prevState;
                }

                PlayerGenerator.initializePlayerIdCounter(prevState as GameState);
                
                const newState: GameState = JSON.parse(JSON.stringify(prevState));
                (newState as any).gameStarted = true;
                const playerTeam = getPlayerTeam(newState);
                if (!playerTeam || playerTeam.squad.length >= MAX_SQUAD_SIZE) {
                    resolve(undefined);
                    return prevState;
                }

                const availableLegends = legends.filter(legend => legend.gender === (newState.gameType === 'womens' ? 'female' : 'male'));

                if (availableLegends.length === 0) {
                    console.warn(`No legends available for game type: ${newState.gameType}`);
                    resolve(undefined);
                    return prevState;
                }

                const legendData = availableLegends[Math.floor(Math.random() * availableLegends.length)];
                
                const allPlayers = Object.values(newState.leagues).flatMap(l => l.teams).flatMap(t => t.squad);
                let newPlayerName = legendData.name;
                let counter = 2;
                while (allPlayers.some(p => p.name === newPlayerName)) {
                    const nameParts = legendData.name.split(' ');
                    const lastName = nameParts.pop();
                    newPlayerName = [...nameParts, `${lastName} ${counter}`].join(' ');
                    counter++;
                }

                const newPlayer: Player = {
                    ...basePlayerStats,
                    ...(legendData as any), // Cast to avoid type mismatch with partial data
                    id: PlayerGenerator.getNextPlayerId(),
                    name: newPlayerName,
                    contract: { yearsRemaining: 2, expirySeason: newState.season + 2 },
                    history: [],
                    developmentHistory: [],
                    injuryHistory: [],
                };

                // FIX: Pass all required arguments to getPlayerWeeklyWage and getPlayerValue
                newPlayer.wage = getPlayerWeeklyWage(newPlayer, newState.playerLeagueName, newState.gameType, newState.config);
                newPlayer.marketValue = getPlayerValue(newPlayer, newState.playerLeagueName, newState.gameType, newState.config);
                
                playerTeam.squad.push(newPlayer);
                const message = newPlayer.isLegend
                    ? `${newPlayer.name} has sensationally come out of retirement to join ${playerTeam.name}!`
                    : `${newPlayer.name} has signed for ${playerTeam.name} in a shocking move!`;
                addNewsItem(newState, 'A Legend Arrives!', message, 'general');
                
                resolve(newPlayer);
                return newState;
            });
        });
    }, [setGameState]);

    const generateGreatYouthIntake = useCallback(() => {
        setGameState(prevState => {
            if (!prevState.gameStarted) return prevState;
            const newState: GameState = JSON.parse(JSON.stringify(prevState));
            (newState as any).gameStarted = true;
            const playerTeam = getPlayerTeam(newState);
            if (playerTeam) {
                const newYouths: Player[] = [];
                const requiredPositions: Position[] = ['GK', 'DEF', 'DEF', 'DEF', 'MID', 'MID', 'MID', 'FWD', 'FWD'];
                requiredPositions.forEach(pos => {
                    // FIX: Pass all required arguments to generateYouthPlayer
                    const p = PlayerGenerator.generateYouthPlayer(playerTeam, pos, newState.playerLeagueName, newState.config);
                    p.potential = Math.max(p.potential, 85 + Math.floor(Math.random() * 10));
                    p.skill = Math.max(p.skill, 50 + Math.floor(Math.random() * 15));
                    newYouths.push(p);
                });
                playerTeam.youthAcademy.squad = newYouths;
                addNewsItem(newState, 'Golden Generation', 'A "golden generation" of youth players has arrived at the academy!', 'youth');
            }
            return newState;
        });
    }, [setGameState]);

    const boostTeamMorale = useCallback(() => {
        setGameState(prevState => {
            if (!prevState.gameStarted) return prevState;
            const newState: GameState = JSON.parse(JSON.stringify(prevState));
            (newState as any).gameStarted = true;
            const playerTeam = getPlayerTeam(newState);
            if (playerTeam) {
                playerTeam.squad.forEach(p => {
                    p.morale = 'Ecstatic';
                    p.moraleReason = 'Feeling fantastic';
                });
                addNewsItem(newState, 'Morale Boost', 'A team-building exercise has left the entire squad feeling ecstatic!', 'morale');
            }
            return newState;
        });
    }, [setGameState]);

    const healAllInjuries = useCallback(() => {
        setGameState(prevState => {
            if (!prevState.gameStarted) return prevState;
            const newState: GameState = JSON.parse(JSON.stringify(prevState));
            (newState as any).gameStarted = true;
            const playerTeam = getPlayerTeam(newState);
            if (playerTeam) {
                playerTeam.squad.forEach(p => {
                    if (p.injury) {
                        p.injury = null;
                    }
                });
                addNewsItem(newState, 'Miraculous Recovery', 'All injured players have made a miraculous recovery and are fit to play!', 'recovery');
            }
            return newState;
        });
    }, [setGameState]);

    const restartWithTeamCount = useCallback(async (teamCount: number) => {
        if (!gameState.gameStarted) return;
        const gameType = gameState.gameType;
        await clearAllSaves();
        setHasSave(false);
        const restrictedGameData = buildRestrictedGameData(gameType, teamCount);
        const newGame = createNewGame(restrictedGameData);
        PlayerGenerator.initializePlayerIdCounter(newGame);
        setGameState(newGame);
        // Note: The autosave effect (useEffect in useGameLogic) will automatically save the new game state
    }, [gameState.gameStarted, gameState.gameType, setGameState, setHasSave]);

    const updateSearchHistory = useCallback((item: SearchHistoryItem) => {
        setGameState(prevState => {
            if (!prevState.gameStarted) return prevState;
            const newState: GameState = JSON.parse(JSON.stringify(prevState));
            (newState as any).gameStarted = true;

            // Initialize searchHistory if it doesn't exist
            if (!newState.searchHistory) {
                newState.searchHistory = [];
            }

            // Remove the item if it already exists (to move it to the front)
            newState.searchHistory = newState.searchHistory.filter(
                h => !(h.type === item.type && h.id === item.id)
            );

            // Add the item to the front
            newState.searchHistory.unshift(item);

            // Keep only the last 3 items
            newState.searchHistory = newState.searchHistory.slice(0, 3);

            return newState;
        });
    }, [setGameState]);

    return {
        markNewsAsRead,
        addPlayerMoney,
        setDebugFlag,
        toggleDebugFlag,
        cacheAiPunditTake,
        cacheAiMatchReport,
        cacheAiPlayerRoleAnalysis,
        addElitePlayer,
        addLegendPlayer,
        generateGreatYouthIntake,
        boostTeamMorale,
        healAllInjuries,
        restartWithTeamCount,
        updateSearchHistory,
    };
};
